import React from 'react';
import _ from 'lodash';
import cn from 'classnames';
import { generatePath, useHistory } from 'react-router-dom';
import SwiperCore, { Virtual, Mousewheel, Navigation } from 'swiper';
import SwiperClass from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { routes } from 'client/constants';
import { useAppDispatch, useAppSelector, useTranslation } from 'client/hooks';
import { IModel } from 'models/fake_mandates_slides/interface';
import { FakeMandateSlide, Loader } from 'client/components/common';

import { selectLang } from 'client/redux/settings/selectors';
import { selectUserRole } from 'client/redux/user/selectors';
import { selectAuth } from 'client/redux/user/selectors';
import { SIZE_TYPE } from 'client/types';

import css from './FakeMandatesSlides.module.scss';
import { setPopup } from 'client/redux/ui';

// install Virtual module
SwiperCore.use([Virtual, Mousewheel, Navigation]);
const SLIDES_PER_GROUP = 4;

type Props = {
  title: string;
  items?: IModel[];
  isLoading: boolean;
};

const FakeMandateSlider: React.FC<Props> = ({ items, isLoading, title }) => {
  const { translate } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector(selectAuth);
  const lang = useAppSelector(selectLang);
  const role = useAppSelector(selectUserRole);
  const sliderRef = React.useRef<SwiperClass>();
  const [isHideNext, setIsHideNext] = React.useState<boolean>(false);
  const [isHidePrev, setIsHidePrev] = React.useState<boolean>(true);

  const onTitleClick = () => {
    const investmentOppLink = generatePath(routes.INVESTMENT_OPPORTUNITIES, { lang });

    if (!isLoggedIn) {
      dispatch(setPopup({ type: 'auth', data: { initialAuthType: 'sign-in', redirectUrl: investmentOppLink } }));
    } else if (role === 'VIEWER' && isLoggedIn) {
      dispatch(
        setPopup({
          type: 'join-as-investor',
          data: { link: generatePath(routes.MAIN_PAGE_ROUTE, { lang }), isExternalLink: false },
        })
      );
    } else if (role !== 'VIEWER') {
      history.push(investmentOppLink);
    }
  };

  return (
    <section className={css.investNow}>
      <h4 role="button" onClick={onTitleClick} className={css.title}>
        {title}
      </h4>

      {isLoading ? (
        <Loader position="absolute" />
      ) : (
        <>
          {items && items.length > 3 ? (
            <div className={css.sliderWrapper}>
              <Swiper
                spaceBetween={0}
                slidesPerView={4}
                mousewheel={false}
                onSwiper={(swiper) => (sliderRef.current = swiper)}
                onSlideChange={(s) => {
                  if (items) {
                    if (s.activeIndex === 0) {
                      setIsHidePrev(true);
                    } else {
                      setIsHidePrev(false);
                    }
                    if (s.activeIndex + SLIDES_PER_GROUP >= items.length) {
                      setIsHideNext(true);
                    } else {
                      setIsHideNext(false);
                    }
                  }
                }}
                slidesPerGroup={SLIDES_PER_GROUP}
              >
                {_.map(items, (i, index) => (
                  <SwiperSlide key={index}>
                    <FakeMandateSlide
                      key={i._id.toString()}
                      item={i}
                      isUpvoteDisabled
                      className={css.slide}
                      isMandateLink
                      buttonLabel={translate('mainPageDesktop.watchButton')}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              {items && items.length > SLIDES_PER_GROUP && (
                <>
                  <div
                    className={cn(css.navBtn, css.next, isHideNext && css.hide)}
                    onClick={() => sliderRef.current?.slideNext()}
                  >
                    <div className={css.arrow}></div>
                  </div>

                  <div
                    className={cn(css.navBtn, css.prev, isHidePrev && css.hide)}
                    onClick={() => sliderRef.current?.slidePrev()}
                  >
                    <div className={css.arrow}></div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={css.grid}>
              {_.map(items, (i) => (
                <FakeMandateSlide
                  key={i._id.toString()}
                  item={i}
                  isUpvoteDisabled
                  size={SIZE_TYPE.L}
                  buttonSize={SIZE_TYPE.M}
                  className={css.slide}
                  isMandateLink
                  buttonLabel={translate('mainPageDesktop.watchButton')}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default FakeMandateSlider;
